import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon,
  People as WorkspaceIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import API_URL from './config';

function OrganizationManagement() {
  const { user: currentUser, isSuperAdmin, impersonate } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgMembers, setOrgMembers] = useState({}); // { orgId: [users] }
  const [loadingOrgMembers, setLoadingOrgMembers] = useState({}); // { orgId: true/false }
  const [expandedOrgs, setExpandedOrgs] = useState({}); // { orgId: true/false }
  
  // Organization dialog state
  const [openOrgDialog, setOpenOrgDialog] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [orgFormData, setOrgFormData] = useState({ name: '' });
  
  // User dialog state
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [currentOrgId, setCurrentOrgId] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'user',
    isActive: true,
    autoGeneratePassword: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [generatedPasswordUsername, setGeneratedPasswordUsername] = useState('');
  const [deleteUserConfirm, setDeleteUserConfirm] = useState({ open: false, user: null, orgId: null });
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/organizations`);
      const orgs = response.data.organizations || [];
      setOrganizations(orgs);
      
      // Set all organizations as expanded by default (collapsed if superadmin)
      const expandedState = {};
      const defaultExpanded = !isSuperAdmin;
      orgs.forEach((org) => {
        const orgId = org.id || org._id;
        expandedState[orgId] = defaultExpanded;
      });
      setExpandedOrgs(expandedState);
      
      // Fetch members for all organizations in parallel and wait for all to complete
      const memberPromises = orgs.map((org) => {
        const orgId = org.id || org._id;
        return fetchOrgMembers(orgId, true);
      });
      
      await Promise.all(memberPromises);
    } catch (error) {
      console.error('Error fetching workspaces:', error);
      showSnackbar('Failed to fetch workspaces', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgMembers = async (orgId, forceRefresh = false) => {
    if (!forceRefresh && orgMembers[orgId]) {
      // Already loaded
      return;
    }

    try {
      setLoadingOrgMembers((prev) => ({ ...prev, [orgId]: true }));
      const response = await axios.get(`${API_URL}/api/organizations/${orgId}/users`);
      setOrgMembers((prev) => ({ ...prev, [orgId]: response.data.users || [] }));
    } catch (error) {
      console.error('Error fetching organization members:', error);
      setOrgMembers((prev) => ({ ...prev, [orgId]: [] }));
    } finally {
      setLoadingOrgMembers((prev) => ({ ...prev, [orgId]: false }));
    }
  };

  const handleOrgAccordionChange = (orgId) => (event, isExpanded) => {
    setExpandedOrgs((prev) => ({ ...prev, [orgId]: isExpanded }));
    if (isExpanded) {
      fetchOrgMembers(orgId);
    }
  };

  // Helper function to generate random password (matches backend logic)
  const generatePassword = (length = 15) => {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    const array = new Uint32Array(length);
    crypto.getRandomValues(array);
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset[array[i] % charset.length];
    }
    return password;
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Organization management functions
  const handleOpenOrgDialog = (org = null) => {
    if (org) {
      setEditingOrg(org);
      setOrgFormData({ name: org.name });
    } else {
      setEditingOrg(null);
      setOrgFormData({ name: '' });
    }
    setOpenOrgDialog(true);
  };

  const handleCloseOrgDialog = () => {
    setOpenOrgDialog(false);
    setEditingOrg(null);
    setOrgFormData({ name: '' });
  };

  const handleSubmitOrganization = async () => {
    try {
      if (!orgFormData.name.trim()) {
        showSnackbar('Workspace name is required', 'error');
        return;
      }

      if (editingOrg) {
        const orgId = editingOrg.id || editingOrg._id;
        await axios.put(`${API_URL}/api/organizations/${orgId}`, {
          name: orgFormData.name.trim(),
        });
        showSnackbar('Workspace updated successfully', 'success');
      } else {
        await axios.post(`${API_URL}/api/organizations`, {
          name: orgFormData.name.trim(),
        });
        showSnackbar('Workspace created successfully', 'success');
      }
      
      await fetchOrganizations();
      handleCloseOrgDialog();
    } catch (error) {
      console.error('Error saving workspace:', error);
      showSnackbar(
        error.response?.data?.error || 'Failed to save workspace',
        'error'
      );
    }
  };

  // User management functions
  const handleOpenUserDialog = (orgId, user = null) => {
    setCurrentOrgId(orgId);
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        password: '',
        role: user.role,
        isActive: user.isActive,
        autoGeneratePassword: false,
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        role: 'user',
        isActive: true,
        autoGeneratePassword: false,
      });
    }
    setShowPassword(false);
    setOpenUserDialog(true);
  };

  const handleCloseUserDialog = () => {
    setOpenUserDialog(false);
    setEditingUser(null);
    setCurrentOrgId(null);
    setFormData({
      username: '',
      password: '',
      role: 'user',
      isActive: true,
      autoGeneratePassword: false,
    });
    setShowPassword(false);
  };

  const handleSubmitUser = async () => {
    try {
      if (!formData.username.trim()) {
        showSnackbar('Username is required', 'error');
        return;
      }

      if (!formData.autoGeneratePassword && !editingUser && !formData.password) {
        showSnackbar('Password is required or enable auto-generate', 'error');
        return;
      }

      if (!formData.autoGeneratePassword && formData.password && formData.password.length < 6) {
        showSnackbar('Password must be at least 6 characters', 'error');
        return;
      }

      const payload = {
        username: formData.username.trim(),
        role: formData.role,
        isActive: formData.isActive,
        autoGeneratePassword: formData.autoGeneratePassword,
        organizationId: currentOrgId,
      };

      if (!formData.autoGeneratePassword && formData.password) {
        payload.password = formData.password;
      } else if (formData.autoGeneratePassword && generatedPassword) {
        payload.password = generatedPassword;
        payload.autoGeneratePassword = false;
      }

      let response;
      if (editingUser) {
        const userId = editingUser.id || editingUser._id;
        if (!userId) {
          showSnackbar('Invalid user ID', 'error');
          return;
        }
        response = await axios.put(`${API_URL}/api/users/${userId}`, payload);
      } else {
        response = await axios.post(`${API_URL}/api/users`, payload);
      }

      if (response.data.user.generatedPassword) {
        setGeneratedPassword(response.data.user.generatedPassword);
        setGeneratedPasswordUsername(response.data.user.username);
      } else if (formData.autoGeneratePassword && generatedPassword) {
        setGeneratedPasswordUsername(response.data.user.username);
      }

      const newUser = response.data.user;
      
      // Optimistically update the UI immediately
      if (!editingUser) {
        // For new users, add to members list immediately
        setOrgMembers((prev) => {
          const currentMembers = prev[currentOrgId] || [];
          return {
            ...prev,
            [currentOrgId]: [newUser, ...currentMembers],
          };
        });
        
        // Optimistically update member count
        setOrganizations(prevOrgs =>
          prevOrgs.map(org => {
            const orgId = org.id || org._id;
            if (orgId === currentOrgId) {
              return { ...org, memberCount: (org.memberCount || 0) + 1 };
            }
            return org;
          })
        );
      } else {
        // For updates, update the user in the members list
        setOrgMembers((prev) => {
          const currentMembers = prev[currentOrgId] || [];
          return {
            ...prev,
            [currentOrgId]: currentMembers.map(user => {
              const userId = user.id || user._id;
              const updatedUserId = newUser.id || newUser._id;
              return userId === updatedUserId ? newUser : user;
            }),
          };
        });
      }

      showSnackbar(
        editingUser ? 'User updated successfully' : 'User created successfully',
        'success'
      );
      
      // Close modal first to prevent glitches
      handleCloseUserDialog();
      
      // Refresh organizations list to update member count (in case of any discrepancies)
      await fetchOrganizations();
      
      // Refresh members for the current organization (force refresh to ensure consistency)
      await fetchOrgMembers(currentOrgId, true);
    } catch (error) {
      console.error('Error saving user:', error);
      showSnackbar(
        error.response?.data?.error || 'Failed to save user',
        'error'
      );
    }
  };

  const handleDeleteUser = async () => {
    try {
      const userId = deleteUserConfirm.user?.id || deleteUserConfirm.user?._id;
      const orgId = deleteUserConfirm.orgId;
      if (!userId) {
        showSnackbar('Invalid user ID', 'error');
        return;
      }
      await axios.delete(`${API_URL}/api/users/${userId}`);
      
      // Optimistically update the UI immediately
      setOrgMembers((prev) => {
        const currentMembers = prev[orgId] || [];
        return {
          ...prev,
          [orgId]: currentMembers.filter(user => {
            const userOrgId = user.id || user._id;
            return userOrgId !== userId;
          }),
        };
      });
      
      // Optimistically update member count
      setOrganizations(prevOrgs =>
        prevOrgs.map(org => {
          const orgIdValue = org.id || org._id;
          if (orgIdValue === orgId) {
            return { ...org, memberCount: Math.max(0, (org.memberCount || 0) - 1) };
          }
          return org;
        })
      );
      
      showSnackbar('User deleted successfully', 'success');
      setDeleteUserConfirm({ open: false, user: null, orgId: null });
      
      // Refresh organizations list to update member count (in case of any discrepancies)
      await fetchOrganizations();
      
      // Refresh members for the organization (force refresh to ensure consistency)
      await fetchOrgMembers(orgId, true);
    } catch (error) {
      console.error('Error deleting user:', error);
      showSnackbar(
        error.response?.data?.error || 'Failed to delete user',
        'error'
      );
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar('Copied to clipboard', 'success');
  };

  const handleImpersonate = async (user) => {
    try {
      const userId = user.id || user._id;
      if (!userId) {
        showSnackbar('Invalid user ID', 'error');
        return;
      }

      const result = await impersonate(userId);
      if (result.success) {
        showSnackbar(`Logged in as ${user.username}`, 'success');
        navigate('/');
      } else {
        showSnackbar(result.error || 'Failed to log in as user', 'error');
      }
    } catch (error) {
      console.error('Error impersonating user:', error);
      showSnackbar('Failed to log in as user', 'error');
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'superAdmin':
        return 'error';
      case 'owner':
        return 'info';
      case 'admin':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'superAdmin':
        return 'Super Admin';
      case 'owner':
        return 'Owner';
      case 'admin':
        return 'Admin';
      default:
        return 'User';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Workspace Management
        </Typography>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenOrgDialog()}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5b5ff0 0%, #7c4ef5 100%)',
              },
            }}
          >
            Create Workspace
          </Button>
        )}
      </Box>

      {organizations.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">No workspaces found</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {organizations.map((org) => {
            const orgId = org.id || org._id;
            const members = orgMembers[orgId] || [];
            const isLoading = loadingOrgMembers[orgId];
            const isExpanded = expandedOrgs[orgId] || false;
            
            return (
              <Accordion
                key={orgId}
                expanded={isExpanded}
                onChange={handleOrgAccordionChange(orgId)}
                sx={{
                  borderRadius: 2,
                  '&:before': { display: 'none' },
                  boxShadow: 2,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <WorkspaceIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                      {org.name}
                    </Typography>
                    <Chip
                      label={`${org.memberCount || 0} member${(org.memberCount || 0) !== 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenUserDialog(orgId);
                      }}
                    >
                      Add User
                    </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenOrgDialog(org);
                        }}
                        sx={{ color: 'primary.main' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                  {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : members.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="text.secondary">No members in this workspace</Typography>
                    </Box>
                  ) : (() => {
                    // Sort members: admin/superAdmin first, then user
                    const sortedMembers = [...members].sort((a, b) => {
                      const roleOrder = { superAdmin: 0, owner: 1, admin: 2, user: 3 };
                      const aOrder = roleOrder[a.role] ?? 4;
                      const bOrder = roleOrder[b.role] ?? 4;
                      return aOrder - bOrder;
                    });
                    
                    return (
                      <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: 'none', overflow: 'hidden' }}>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                              <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                              <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedMembers.map((user, index) => {
                              const userId = user.id || user._id;
                              const currentUserId = currentUser?.id || currentUser?._id;
                              const isLastRow = index === sortedMembers.length - 1;
                            return (
                              <TableRow 
                                key={userId} 
                                hover
                                sx={isLastRow ? {
                                  '&:last-child td:first-of-type': {
                                    borderBottomLeftRadius: '8px',
                                  },
                                  '&:last-child td:last-of-type': {
                                    borderBottomRightRadius: '8px',
                                  },
                                } : {}}
                              >
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {user.username}
                                    {userId === currentUserId && (
                                      <Chip
                                        label="You"
                                        size="small"
                                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                      />
                                    )}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={getRoleLabel(user.role)}
                                    color={getRoleColor(user.role)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={user.isActive ? 'Active' : 'Inactive'}
                                    color={user.isActive ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {new Date(user.createdAt).toLocaleDateString()}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    {isSuperAdmin && userId !== currentUserId && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleImpersonate(user)}
                                        sx={{
                                          textTransform: 'none',
                                          fontSize: '0.8125rem',
                                          fontWeight: 500,
                                          px: 2,
                                          py: 0.75,
                                          minWidth: 'auto',
                                          borderRadius: 1.5,
                                          borderColor: 'rgba(33, 150, 243, 0.5)',
                                          color: 'info.main',
                                          backgroundColor: 'rgba(33, 150, 243, 0.06)',
                                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                                          '&:hover': {
                                            borderColor: 'info.main',
                                            backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                            color: 'info.dark',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 2px 6px rgba(33, 150, 243, 0.15)',
                                          },
                                          '&:active': {
                                            transform: 'translateY(0)',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                                          },
                                          transition: 'all 0.2s ease',
                                        }}
                                      >
                                        Login as User
                                      </Button>
                                    )}
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenUserDialog(orgId, user)}
                                      sx={{ color: 'primary.main' }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    {userId !== currentUserId && (
                                      <IconButton
                                        size="small"
                                        onClick={() => setDeleteUserConfirm({ open: true, user, orgId })}
                                        sx={{ color: 'error.main' }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    );
                  })()}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {/* Create/Edit Organization Dialog */}
      <Dialog open={openOrgDialog} onClose={handleCloseOrgDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingOrg ? 'Edit Workspace' : 'Create New Workspace'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Workspace Name"
              value={orgFormData.name}
              onChange={(e) => setOrgFormData({ ...orgFormData, name: e.target.value })}
              fullWidth
              required
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOrgDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitOrganization}
            variant="contained"
            disabled={!orgFormData.name.trim()}
          >
            {editingOrg ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit User Dialog */}
      <Dialog open={openUserDialog} onClose={handleCloseUserDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              fullWidth
              required
              disabled={!!editingUser}
            />

            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                label="Role"
              >
                {isSuperAdmin && <MenuItem value="admin">Admin</MenuItem>}
                {isSuperAdmin && <MenuItem value="owner">Owner</MenuItem>}
                <MenuItem value="user">User</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.autoGeneratePassword}
                  onChange={(e) => {
                    const isEnabled = e.target.checked;
                    setFormData({ ...formData, autoGeneratePassword: isEnabled });
                    if (isEnabled) {
                      const newPassword = generatePassword();
                      setGeneratedPassword(newPassword);
                      if (!editingUser) {
                        setGeneratedPasswordUsername(formData.username || 'New User');
                      } else {
                        setGeneratedPasswordUsername(formData.username);
                      }
                    } else {
                      setGeneratedPassword('');
                      setGeneratedPasswordUsername('');
                    }
                  }}
                />
              }
              label="Auto-generate password"
            />

            {!formData.autoGeneratePassword && (
              <TextField
                label={editingUser ? 'New Password (leave empty to keep current)' : 'Password'}
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                fullWidth
                required={!editingUser}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
              />
            )}

            {formData.autoGeneratePassword && generatedPassword && (
              <Alert
                severity="info"
                action={
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(generatedPassword)}
                    color="inherit"
                    title="Copy password"
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                }
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Generated Password (copy this now, it won't be shown again):
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography
                    variant="body1"
                    onClick={() => copyToClipboard(generatedPassword)}
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 600,
                      wordBreak: 'break-all',
                      cursor: 'pointer',
                      userSelect: 'all',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        borderRadius: 1,
                      },
                      px: 1,
                      py: 0.5,
                    }}
                    title="Click to copy"
                  >
                    {generatedPassword}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CopyIcon />}
                    onClick={() => copyToClipboard(generatedPassword)}
                    sx={{ minWidth: 'auto' }}
                  >
                    Copy
                  </Button>
                </Box>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={handleCloseUserDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitUser}
            variant="contained"
            disabled={generatedPassword ? false : !formData.username.trim()}
          >
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteUserConfirm.open} onClose={() => setDeleteUserConfirm({ open: false, user: null, orgId: null })}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user <strong>{deleteUserConfirm.user?.username}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUserConfirm({ open: false, user: null, orgId: null })}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default OrganizationManagement;

